/**
 * TERRAIN LOCAL SURGERY (2026-08-15 round — docs/MODELER.md "TERRAIN LOCAL
 * SURGERY"). Matt's conceptual model, adopted verbatim as the design: stop
 * re-triangulating the terrain globally for a graded-to slab — operate on the
 * EXISTING TIN with true-edge surgery instead: project the boundary, split the
 * crossed facets so it becomes TRUE mesh edges, and stitch vertical faces.
 *
 * COMPOSITION (the tear fix, same day — Matt's Forsyth screenshot: white tears
 * along vertical faces): the first cut of this round applied per-modifier
 * surgeries SEQUENTIALLY, each duplicating seam vertices and adding degenerate
 * band triangles that the NEXT surgery's split then aliased (same-xy duplicate
 * vertices — locate/dedupe picks one sheet, the other never splits → open
 * edges). Forsyth's standard configuration — retaining walls exactly on slab
 * edges, slabs sharing edge lines — is maximally coincident, and the live model
 * showed 423 torn edges. The rebuilt pipeline separates concerns so surgeries
 * can never interfere:
 *
 *   1. SPLIT every seam path (every retaining line, every referencing-pad ring)
 *      on the clean base first — `splitTinAlongPath` composes perfectly with
 *      itself (no duplicates, no degenerates created; coincident paths resolve
 *      to the SAME true edges; a second pass over an already-split path is
 *      idempotent and just reads the chain back).
 *   2. RE-ELEVATE pad interiors in place (no delete/re-add — the cap is the
 *      original facets at the pad elevation).
 *   3. RESOLVE SEAMS once (`resolveSeams`): around each seam vertex, group the
 *      incident facets by connectivity that doesn't cross a seam edge; give
 *      each group its own z (the graded surface sampled a hair into the group —
 *      keeping the vertex's exact interpolated z when the sample agrees, so the
 *      continuous side stays byte-planar); merge groups whose z agree; give the
 *      rest same-xy copies; and stitch ONE vertical band per seam edge between
 *      the two sides' copies, wound toward the lower side.
 *
 * Wall-along-ring, ring-along-ring, wall×wall corners, walls ending mid-terrain
 * (the fan stays one group → pinched, watertight), and overlapping pads (both
 * sides of the buried ring sample equal z → merged → the buried cliff vanishes)
 * all fall out of the one mechanism.
 *
 * The load-bearing invariant (unchanged): with only referencing pads live,
 * every triangle OUTSIDE the surgery is BYTE-IDENTICAL to the input TIN — which
 * is what makes the existing/proposed contour overlay coincide exactly wherever
 * grading didn't move the surface.
 *
 * 2.5D note: a TopoTin is xy + one z per vertex; vertical faces are triangles
 * whose xy projection is degenerate (two vertices share xy at different z).
 * Every consumer already tolerates those — tinZAt/meshTopZAt skip near-zero
 * plan-area triangles, and contour extraction WANTS them (a z-plane crossing a
 * vertical band traces the cliff line in plan, exactly the drafting read).
 *
 * Recorded limitations (deliberate):
 *  - A boundary crossing the TIN hull closes visually through the hull skirt
 *    rather than being formally watertight there; a hull vertex on a seam keeps
 *    one side's z for the skirt.
 *  - Seam-vertex group z is sampled 0.02' into the group; when it disagrees
 *    with the vertex's exact interpolated z by more than KEEP_TOL the sampled
 *    value wins (a sub-inch warp at very steep cliff shoulders, never a crack).
 */
import earcut from 'earcut'
import type { Pt2 } from '@aj/bim-kernel'

/** structurally identical to tin.ts's TopoTin — declared locally so the import
 *  graph stays one-directional (tin.ts imports surgery, never the reverse) */
export interface SurgeryTin {
  pts: Pt2[]
  zs: number[]
  tris: number[]
  hull: number[]
}

/** coincidence snap for boundary vertices landing on existing TIN vertices/edges */
const SNAP = 1e-6
/** EVENT snap — the radius within which a path event reuses an EXISTING vertex
 *  instead of minting a new one. Split calls dedupe their own new vertices, but
 *  coincident rings split in SEPARATE calls mint near-duplicates (ill-conditioned
 *  intersections land 1e-5..1e-4 apart), and same-xy near-dups alias the seam
 *  machinery into tears. Nothing legitimate is 0.03mm from something else. */
const EVENT_SNAP = 1e-4
/** looser on-segment tolerance for chaining existing vertices that lie ON a
 *  path — near-collinear seams cross at shallow angles whose intersection
 *  points carry more float error than SNAP; a vertex this close to the path
 *  belongs to its chain (the seam follows true mesh edges either way) */
const ON_SEG = 1e-4
/** degenerate-triangle reject (sq ft, plan area ×2) */
const AREA2_EPS = 1e-9
/** side-sample offset for the retaining step's per-side z reads */
const SIDE_OFF = 0.05

function pointInRing(pt: Pt2, ring: Pt2[]): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j]
    if ((yi > pt[1]) !== (yj > pt[1]) &&
        pt[0] < ((xj - xi) * (pt[1] - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}

/** twice the signed area, positive = counter-clockwise in y-up world XY */
function area2(a: Pt2, b: Pt2, c: Pt2): number {
  return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0])
}

function segsCross(a: Pt2, b: Pt2, c: Pt2, d: Pt2): boolean {
  // the area epsilon matters: near-collinear segments (a seam path vs its own
  // true-edge chain) have cross products at float-noise scale whose sign
  // products randomly read as "proper crossing", minting ill-conditioned
  // intersection points (the Forsyth chain breaks). Noise-collinear = no cross;
  // a genuine crossing this close to an endpoint is the endpoint's event.
  const EPS_O = 1e-7
  const o1 = area2(a, b, c), o2 = area2(a, b, d)
  if (o1 * o2 >= 0 || Math.min(Math.abs(o1), Math.abs(o2)) < EPS_O) return false
  const o3 = area2(c, d, a), o4 = area2(c, d, b)
  return o3 * o4 < 0 && Math.min(Math.abs(o3), Math.abs(o4)) >= EPS_O
}

// ---------- the split engine ----------

interface Located {
  kind: 'vertex' | 'edge' | 'tri'
  /** vertex: the id */
  id?: number
  /** edge: canonical key + endpoints + param s from the lower-id endpoint */
  edgeKey?: number
  eu?: number
  ev?: number
  s?: number
  /** tri: facet index (into tris/3) + barycentric weights */
  fi?: number
  w?: [number, number, number]
}

export interface SplitResult {
  tin: SurgeryTin
  /**
   * Ordered vertex ids along the path once it is true mesh edges; -1 marks a
   * break (the path left the mesh — hull exit, or a rigid degenerate facet).
   * For a closed path that never leaves the mesh the first id re-appears at the
   * end, closing the loop.
   */
  chain: number[]
  /** facet indices (original tris/3 positions) whose triangles were replaced */
  touched: Set<number>
}

const edgeKeyOf = (a: number, b: number): string => (a < b ? `${a}_${b}` : `${b}_${a}`)

/** single-path convenience over splitTinAlongPaths (tests + region seams) */
export function splitTinAlongPath(
  tin: SurgeryTin,
  path: Pt2[],
  closed: boolean,
  readOnly = false,
): SplitResult {
  const res = splitTinAlongPaths(tin, [{ pts: path, closed }], readOnly)
  return { tin: res.tin, chain: res.chains[0] ?? [], touched: res.touched }
}

export interface MultiSplitResult {
  tin: SurgeryTin
  /** one chain per input path (same -1 break convention as SplitResult) */
  chains: number[][]
  touched: Set<number>
}

/**
 * Split the TIN's facets along EVERY path in one pass so each becomes true mesh
 * edges. Pure — returns fresh arrays; untouched facets keep their exact triples
 * in their original positions (the byte-identity invariant). Hull edges split
 * by a path get their new vertices spliced into the hull ring.
 *
 * BATCHED on purpose (terrain perf round, 2026-08-15): the per-call table setup
 * (facet/edge/vertex grids) dominated when foundation cutting fed dozens of
 * rings, and sequential calls also meant a later path could subdivide an
 * earlier path's chain edges (staleness the seam pass then had to repair).
 * One call = one setup, one shared vertex registry (no cross-call same-xy
 * near-duplicates by construction), and chains that reference the FINAL mesh
 * exactly. Path×path PROPER CROSSINGS are precomputed as events on both paths,
 * so two rings crossing inside one facet meet at a shared vertex — the
 * per-facet retriangulation's multi-chain machinery needs chords to never
 * cross mid-span, and this guarantees it.
 *
 * `readOnly` (chain extraction): never mints a vertex or touches a facet —
 * every event either snaps to an EXISTING vertex or breaks the chain, and the
 * input tin is returned as-is.
 */
export function splitTinAlongPaths(
  tin: SurgeryTin,
  pathsIn: { pts: Pt2[]; closed: boolean }[],
  readOnly = false,
): MultiSplitResult {
  // read-only extraction never mints vertices — share the input arrays
  const pts: Pt2[] = readOnly ? tin.pts : tin.pts.map((p) => [p[0], p[1]])
  const zs: number[] = readOnly ? tin.zs : tin.zs.slice()
  const nFacets = (tin.tris.length / 3) | 0
  // hot-table keys are NUMERIC (TERRAIN FOUNDATION CUT perf round): this
  // function runs dozens of times per generate now, and per-call string-keyed
  // maps over every edge/facet were the dominant cost
  const EKEY = (a: number, b: number): number => (a < b ? a * 0x200000 + b : b * 0x200000 + a)

  // facet table with bboxes; degenerate facets (earlier surgery's vertical
  // bands) are rigid — excluded from location and never rebuilt
  const facet = (fi: number): [number, number, number] =>
    [tin.tris[fi * 3], tin.tris[fi * 3 + 1], tin.tris[fi * 3 + 2]]
  const rigid: boolean[] = new Array(nFacets)
  const fBbox: [number, number, number, number][] = new Array(nFacets)
  for (let fi = 0; fi < nFacets; fi++) {
    const [a, b, c] = facet(fi)
    const A = pts[a], B = pts[b], C = pts[c]
    rigid[fi] = Math.abs(area2(A, B, C)) < AREA2_EPS
    fBbox[fi] = [
      Math.min(A[0], B[0], C[0]), Math.min(A[1], B[1], C[1]),
      Math.max(A[0], B[0], C[0]), Math.max(A[1], B[1], C[1]),
    ]
  }

  // undirected edge table (non-rigid facets only)
  const edges = new Map<number, { u: number; v: number }>()
  for (let fi = 0; fi < nFacets; fi++) {
    if (rigid[fi]) continue
    const [a, b, c] = facet(fi)
    for (const [u, v] of [[a, b], [b, c], [c, a]] as [number, number][]) {
      const k = EKEY(u, v)
      if (!edges.has(k)) edges.set(k, { u: Math.min(u, v), v: Math.max(u, v) })
    }
  }

  // ---- spatial buckets (TERRAIN FOUNDATION CUT perf round): locate(), the
  // crossing scan, and the on-segment vertex scan were all linear sweeps —
  // fine for a handful of pad rings, quadratic pain once foundation cutting
  // feeds dozens of rings per generate. Facets/edges bucket by bbox into a
  // uniform grid; entries spanning too many cells (a sparse survey's giant
  // facets) go to a small always-checked overflow list instead of flooding
  // the grid. Results are byte-identical — buckets only narrow the pools.
  let gx0 = Infinity, gy0 = Infinity, gx1 = -Infinity, gy1 = -Infinity
  for (const [x, y] of tin.pts) {
    if (x < gx0) gx0 = x
    if (x > gx1) gx1 = x
    if (y < gy0) gy0 = y
    if (y > gy1) gy1 = y
  }
  const CELL = Math.max((Math.max(gx1 - gx0, gy1 - gy0) || 1) / 96, 0.5)
  const OVERFLOW_CELLS = 64
  // numeric cell keys (this function runs dozens of times per generate — string
  // keys across every facet/edge were measurable); ±512-cell headroom covers
  // any path point off the tin's own bbox
  const CKEY = (cx: number, cy: number): number => (cx + 512) * 0x10000 + (cy + 512)
  const cellOf = (x: number, y: number): number =>
    CKEY(Math.floor((x - gx0) / CELL), Math.floor((y - gy0) / CELL))
  const facetGrid = new Map<number, number[]>()
  const facetOverflow: number[] = []
  const edgeGrid = new Map<number, number[]>()
  const edgeOverflow: number[] = []
  const vertGrid = new Map<number, number[]>()
  const bucketRange = (
    x0: number, y0: number, x1: number, y1: number,
    add: (cell: number) => void, overflow: () => void,
  ): void => {
    const c0x = Math.floor((x0 - gx0) / CELL), c1x = Math.floor((x1 - gx0) / CELL)
    const c0y = Math.floor((y0 - gy0) / CELL), c1y = Math.floor((y1 - gy0) / CELL)
    if ((c1x - c0x + 1) * (c1y - c0y + 1) > OVERFLOW_CELLS) { overflow(); return }
    for (let cx = c0x; cx <= c1x; cx++) {
      for (let cy = c0y; cy <= c1y; cy++) add(CKEY(cx, cy))
    }
  }
  for (let fi = 0; fi < nFacets; fi++) {
    if (rigid[fi]) continue
    const [x0, y0, x1, y1] = fBbox[fi]
    bucketRange(x0, y0, x1, y1,
      (cell) => {
        const l = facetGrid.get(cell)
        if (l) l.push(fi)
        else facetGrid.set(cell, [fi])
      },
      () => facetOverflow.push(fi))
  }
  for (const [k, e] of edges) {
    const U = pts[e.u], V = pts[e.v]
    bucketRange(Math.min(U[0], V[0]), Math.min(U[1], V[1]), Math.max(U[0], V[0]), Math.max(U[1], V[1]),
      (cell) => {
        const l = edgeGrid.get(cell)
        if (l) l.push(k)
        else edgeGrid.set(cell, [k])
      },
      () => edgeOverflow.push(k))
  }
  for (let vi = 0; vi < tin.pts.length; vi++) {
    const cell = cellOf(tin.pts[vi][0], tin.pts[vi][1])
    const l = vertGrid.get(cell)
    if (l) l.push(vi)
    else vertGrid.set(cell, [vi])
  }
  /** facet candidates near a point (its cell ± the SNAP fringe) + the overflow */
  const facetsNear = (p: Pt2): number[] => {
    const out = [...facetOverflow]
    const seen = new Set(out)
    for (const dx of [-SNAP, 0, SNAP]) {
      for (const dy of [-SNAP, 0, SNAP]) {
        for (const fi of facetGrid.get(cellOf(p[0] + dx, p[1] + dy)) ?? []) {
          if (!seen.has(fi)) { seen.add(fi); out.push(fi) }
        }
      }
    }
    return out
  }
  /** candidate keys/ids whose buckets intersect a bbox, plus the overflow */
  const inBbox = <T>(
    grid: Map<number, T[]>, overflow: T[],
    x0: number, y0: number, x1: number, y1: number,
  ): T[] => {
    const out = [...overflow]
    const seen = new Set(out)
    const c0x = Math.floor((x0 - gx0) / CELL), c1x = Math.floor((x1 - gx0) / CELL)
    const c0y = Math.floor((y0 - gy0) / CELL), c1y = Math.floor((y1 - gy0) / CELL)
    for (let cx = c0x; cx <= c1x; cx++) {
      for (let cy = c0y; cy <= c1y; cy++) {
        for (const t of grid.get(CKEY(cx, cy)) ?? []) {
          if (!seen.has(t)) { seen.add(t); out.push(t) }
        }
      }
    }
    return out
  }

  function locate(p: Pt2): Located | null {
    for (const fi of facetsNear(p)) {
      if (rigid[fi]) continue
      const [x0, y0, x1, y1] = fBbox[fi]
      if (p[0] < x0 - SNAP || p[0] > x1 + SNAP || p[1] < y0 - SNAP || p[1] > y1 + SNAP) continue
      const [ia, ib, ic] = facet(fi)
      const A = pts[ia], B = pts[ib], C = pts[ic]
      // vertex snap first (EVENT_SNAP — reuse existing vertices, never mint a
      // same-xy near-duplicate)
      for (const [id, P] of [[ia, A], [ib, B], [ic, C]] as [number, Pt2][]) {
        if (Math.hypot(p[0] - P[0], p[1] - P[1]) <= EVENT_SNAP) return { kind: 'vertex', id }
      }
      const denom = area2(A, B, C)
      const w0 = area2(B, C, p) / denom
      const w1 = area2(C, A, p) / denom
      const w2 = 1 - w0 - w1
      const tol = 1e-9
      if (w0 < -tol || w1 < -tol || w2 < -tol) continue
      // edge snap: distance to each edge
      for (const [u, v] of [[ia, ib], [ib, ic], [ic, ia]] as [number, number][]) {
        const U = pts[u], V = pts[v]
        const dx = V[0] - U[0], dy = V[1] - U[1]
        const len2 = dx * dx + dy * dy
        if (len2 < 1e-18) continue
        const t = ((p[0] - U[0]) * dx + (p[1] - U[1]) * dy) / len2
        if (t <= 0 || t >= 1) continue
        const d = Math.hypot(p[0] - (U[0] + dx * t), p[1] - (U[1] + dy * t))
        if (d <= SNAP) {
          const lo = Math.min(u, v)
          const s = lo === u ? t : 1 - t
          return { kind: 'edge', edgeKey: EKEY(u, v), eu: lo, ev: Math.max(u, v), s }
        }
      }
      return { kind: 'tri', fi, w: [w0, w1, w2] }
    }
    return null
  }

  // event registry: dedupe new vertices by rounded coordinate key so the corner
  // event and a float-noise near-crossing can never mint two vertices
  const newByKey = new Map<string, number>()
  const coordKey = (x: number, y: number) => `${Math.round(x * 1e5)},${Math.round(y * 1e5)}`
  const edgeSplits = new Map<number, { id: number; s: number }[]>()
  const interiorPts = new Map<number, number[]>()
  const addVertex = (x: number, y: number, z: number): number => {
    const k = coordKey(x, y)
    const got = newByKey.get(k)
    if (got !== undefined) return got
    // last guard against same-xy near-duplicates: reuse any PRE-EXISTING vertex
    // within EVENT_SNAP (crossings computed against different edges of
    // coincident rings can land a hair apart across calls)
    for (const vi of vertGrid.get(cellOf(x, y)) ?? []) {
      const P = pts[vi]
      if (Math.hypot(P[0] - x, P[1] - y) <= EVENT_SNAP) return vi
    }
    const id = pts.length
    pts.push([x, y])
    zs.push(z)
    newByKey.set(k, id)
    return id
  }
  const eventId = (p: Pt2, loc: Located): number | null => {
    if (loc.kind === 'vertex') return loc.id!
    if (readOnly) return null   // chain extraction: existing vertices only
    if (loc.kind === 'edge') {
      const { eu, ev, s, edgeKey } = loc
      const U = pts[eu!], V = pts[ev!]
      const x = U[0] + (V[0] - U[0]) * s!, y = U[1] + (V[1] - U[1]) * s!
      const z = zs[eu!] + (zs[ev!] - zs[eu!]) * s!
      const id = addVertex(x, y, z)
      let list = edgeSplits.get(edgeKey!)
      if (!list) { list = []; edgeSplits.set(edgeKey!, list) }
      if (!list.some((e) => e.id === id)) list.push({ id, s: s! })
      return id
    }
    const [ia, ib, ic] = facet(loc.fi!)
    const [w0, w1, w2] = loc.w!
    const z = w0 * zs[ia] + w1 * zs[ib] + w2 * zs[ic]
    const id = addVertex(p[0], p[1], z)
    let list = interiorPts.get(loc.fi!)
    if (!list) { list = []; interiorPts.set(loc.fi!, list) }
    if (!list.includes(id)) list.push(id)
    return id
  }

  // segment lists per path (zero-length dropped) + PATH×PATH proper crossings —
  // each crossing becomes an event on BOTH paths, so chords from different
  // paths meet at a shared vertex instead of crossing mid-span
  interface Seg { a: Pt2; b: Pt2 }
  const pathSegs: Seg[][] = pathsIn.map(({ pts: p, closed }) => {
    const segs: Seg[] = []
    const n = closed ? p.length : p.length - 1
    for (let i = 0; i < n; i++) {
      const a = p[i], b = p[(i + 1) % p.length]
      if (Math.hypot(b[0] - a[0], b[1] - a[1]) >= SNAP) segs.push({ a, b })
    }
    return segs
  })
  const crossEvents = new Map<number, { t: number; pt: Pt2 }[]>()
  const segKey = (pi: number, si: number) => pi * 0x10000 + si
  for (let pi = 0; pi < pathSegs.length; pi++) {
    for (let pj = pi + 1; pj < pathSegs.length; pj++) {
      for (let si = 0; si < pathSegs[pi].length; si++) {
        const A = pathSegs[pi][si]
        for (let sj = 0; sj < pathSegs[pj].length; sj++) {
          const B = pathSegs[pj][sj]
          if (!segsCross(A.a, A.b, B.a, B.b)) continue
          const rX = A.b[0] - A.a[0], rY = A.b[1] - A.a[1]
          const qX = B.b[0] - B.a[0], qY = B.b[1] - B.a[1]
          const denom = rX * qY - rY * qX
          if (Math.abs(denom) < 1e-18) continue
          const t = ((B.a[0] - A.a[0]) * qY - (B.a[1] - A.a[1]) * qX) / denom
          const s = ((B.a[0] - A.a[0]) * rY - (B.a[1] - A.a[1]) * rX) / denom
          const x = A.a[0] + rX * t, y = A.a[1] + rY * t
          for (const [k, tt] of [[segKey(pi, si), t], [segKey(pj, sj), s]] as [number, number][]) {
            const list = crossEvents.get(k)
            if (list) list.push({ t: tt, pt: [x, y] })
            else crossEvents.set(k, [{ t: tt, pt: [x, y] }])
          }
        }
      }
    }
  }

  // walk each path: per sub-segment, the start-point event plus every proper
  // edge crossing (and path×path crossing) sorted along it. Endpoint-coincident
  // crossings collapse into vertex events (the "boundary vertex lands exactly
  // on a TIN edge/vertex" edge case); collinear overlap with an edge yields no
  // crossings — the endpoints' edge events carry it.
  interface RawEvent { id: number | null; pt: Pt2 }
  const preExistingVerts = tin.pts.length
  const chords = new Map<number, [number, number][]>()
  const chains: number[][] = []
  for (let pathIdx = 0; pathIdx < pathsIn.length; pathIdx++) {
  const { closed } = pathsIn[pathIdx]
  const segs = pathSegs[pathIdx]
  const raw: RawEvent[] = []
  for (let si = 0; si < segs.length; si++) {
    const { a, b } = segs[si]
    const locA = locate(a)
    raw.push({ id: locA ? eventId(a, locA) : null, pt: a })
    const crossings: { t: number; pt: Pt2; loc: Located }[] = []
    for (const ce of crossEvents.get(segKey(pathIdx, si)) ?? []) {
      const loc = locate(ce.pt)
      if (loc) crossings.push({ t: ce.t, pt: ce.pt, loc })
    }
    const sMinX = Math.min(a[0], b[0]) - SNAP, sMaxX = Math.max(a[0], b[0]) + SNAP
    const sMinY = Math.min(a[1], b[1]) - SNAP, sMaxY = Math.max(a[1], b[1]) + SNAP
    // existing vertices lying ON the sub-segment (a boundary running along a
    // collinear run of mesh edges — e.g. a retaining wall exactly on a survey
    // grid line — produces no strict crossings, and skipping these would let the
    // chain jump the whole run): each becomes a vertex event at its param t
    const abLen2 = (b[0] - a[0]) ** 2 + (b[1] - a[1]) ** 2
    const onSegSeen = new Set<string>()
    // ascending-id order preserved (the same-xy dedupe keeps the LOWEST id,
    // exactly as the pre-bucketing linear scan did)
    const vertCands = inBbox(vertGrid, [], sMinX, sMinY, sMaxX, sMaxY).sort((x, y) => x - y)
    for (const vi of vertCands) {
      if (vi >= preExistingVerts) continue
      const P = pts[vi]
      if (P[0] < sMinX || P[0] > sMaxX || P[1] < sMinY || P[1] > sMaxY) continue
      const t = ((P[0] - a[0]) * (b[0] - a[0]) + (P[1] - a[1]) * (b[1] - a[1])) / abLen2
      if (t <= 0 || t >= 1) continue
      const px = a[0] + (b[0] - a[0]) * t, py = a[1] + (b[1] - a[1]) * t
      if (Math.hypot(P[0] - px, P[1] - py) > ON_SEG) continue
      if (Math.hypot(P[0] - a[0], P[1] - a[1]) <= SNAP || Math.hypot(P[0] - b[0], P[1] - b[1]) <= SNAP) continue
      const k = `${Math.round(P[0] * 1e5)},${Math.round(P[1] * 1e5)}`
      if (onSegSeen.has(k)) continue   // same-xy duplicates from earlier surgery
      onSegSeen.add(k)
      crossings.push({ t, pt: [P[0], P[1]], loc: { kind: 'vertex', id: vi } })
    }
    for (const key of inBbox(edgeGrid, edgeOverflow, sMinX, sMinY, sMaxX, sMaxY)) {
      const e = edges.get(key)!
      const U = pts[e.u], V = pts[e.v]
      if (Math.max(U[0], V[0]) < sMinX || Math.min(U[0], V[0]) > sMaxX
        || Math.max(U[1], V[1]) < sMinY || Math.min(U[1], V[1]) > sMaxY) continue
      if (!segsCross(a, b, U, V)) continue
      // line-line intersection params
      const rX = b[0] - a[0], rY = b[1] - a[1]
      const qX = V[0] - U[0], qY = V[1] - U[1]
      const denom = rX * qY - rY * qX
      if (Math.abs(denom) < 1e-18) continue
      const t = ((U[0] - a[0]) * qY - (U[1] - a[1]) * qX) / denom
      const s = ((U[0] - a[0]) * rY - (U[1] - a[1]) * rX) / denom
      const x = a[0] + rX * t, y = a[1] + rY * t
      // crossing at (or within snap of) an edge endpoint → vertex event
      if (Math.hypot(x - U[0], y - U[1]) <= EVENT_SNAP) {
        crossings.push({ t, pt: [U[0], U[1]], loc: { kind: 'vertex', id: e.u } })
      } else if (Math.hypot(x - V[0], y - V[1]) <= EVENT_SNAP) {
        crossings.push({ t, pt: [V[0], V[1]], loc: { kind: 'vertex', id: e.v } })
      } else {
        crossings.push({ t, pt: [x, y], loc: { kind: 'edge', edgeKey: key, eu: e.u, ev: e.v, s } })
      }
    }
    crossings.sort((p, q) => p.t - q.t)
    for (const c of crossings) raw.push({ id: eventId(c.pt, c.loc), pt: c.pt })
  }
  if (!closed && segs.length) {
    const b = segs[segs.length - 1].b
    const locB = locate(b)
    raw.push({ id: locB ? eventId(b, locB) : null, pt: b })
  } else if (closed && raw.length) {
    raw.push(raw[0])   // close the loop
  }

  // chain + per-facet chord assignment. A chord is a chain edge whose midpoint
  // sits strictly inside one facet (midpoint ON an edge = the chord already IS
  // a mesh edge — nothing to split, but it stays in the chain for the band).
  const chain: number[] = []
  let prev: RawEvent | null = null
  for (const ev of raw) {
    if (ev.id === null) {
      if (chain.length && chain[chain.length - 1] !== -1) chain.push(-1)
      prev = null
      continue
    }
    if (prev && prev.id === ev.id) continue
    if (prev) {
      const mid: Pt2 = [(prev.pt[0] + ev.pt[0]) / 2, (prev.pt[1] + ev.pt[1]) / 2]
      const locM = locate(mid)
      if (!locM) {
        if (chain.length && chain[chain.length - 1] !== -1) chain.push(-1)
        chain.push(ev.id)
        prev = ev
        continue
      }
      if (locM.kind === 'tri') {
        let list = chords.get(locM.fi!)
        if (!list) { list = []; chords.set(locM.fi!, list) }
        list.push([prev.id!, ev.id])
      }
      chain.push(ev.id)
    } else {
      chain.push(ev.id)
    }
    prev = ev
  }
  chains.push(chain)
  }   // per-path walk

  if (readOnly) return { tin, chains, touched: new Set() }

  // rebuild facets
  const touched = new Set<number>()
  const outTris: number[] = []
  const splitsOn = (u: number, v: number): { id: number; s: number }[] => {
    const list = edgeSplits.get(EKEY(u, v))
    if (!list) return []
    // sort along u→v (list s params run from the lower id)
    const fromU = Math.min(u, v) === u
    return list.slice().sort((a, b) => (fromU ? a.s - b.s : b.s - a.s))
  }
  for (let fi = 0; fi < nFacets; fi++) {
    const [ia, ib, ic] = facet(fi)
    const hasSplits = !rigid[fi] && (
      edgeSplits.has(EKEY(ia, ib)) || edgeSplits.has(EKEY(ib, ic))
      || edgeSplits.has(EKEY(ic, ia)) || interiorPts.has(fi) || chords.has(fi))
    if (!hasSplits) {
      outTris.push(ia, ib, ic)   // byte-identical, original position
      continue
    }
    touched.add(fi)
    retriangulateFacet(
      [ia, ib, ic],
      splitsOn(ia, ib).map((e) => e.id), splitsOn(ib, ic).map((e) => e.id), splitsOn(ic, ia).map((e) => e.id),
      interiorPts.get(fi) ?? [], chords.get(fi) ?? [],
      pts, outTris,
    )
  }

  // hull: splice split points into split hull edges, preserving orientation
  const hull: number[] = []
  for (let i = 0; i < tin.hull.length; i++) {
    const u = tin.hull[i], v = tin.hull[(i + 1) % tin.hull.length]
    hull.push(u)
    for (const e of splitsOn(u, v)) hull.push(e.id)
  }

  return { tin: { pts, zs, tris: outTris, hull }, chains, touched }
}

/**
 * Re-triangulate one facet whose boundary/interior gained path vertices, so
 * every path sub-segment inside it becomes a triangle edge. The facet is convex
 * (a triangle), so the path through it is a set of chord chains: each
 * boundary-to-boundary chain splits the region polygon in two; a closed loop
 * fully inside becomes a hole + island (the "pad corners inside a single
 * facet" case); a dangling end (an open retaining line stopping mid-facet)
 * gets an artificial chord to the nearest safe boundary vertex.
 */
function retriangulateFacet(
  corners: [number, number, number],
  splitsAB: number[], splitsBC: number[], splitsCA: number[],
  interior: number[], chordList: [number, number][],
  pts: Pt2[], outTris: number[],
): void {
  const loop: number[] = [
    corners[0], ...splitsAB,
    corners[1], ...splitsBC,
    corners[2], ...splitsCA,
  ]
  const boundary = new Set(loop)

  // build chains from the chord graph
  const adj = new Map<number, number[]>()
  const addAdj = (a: number, b: number) => {
    let l = adj.get(a)
    if (!l) { l = []; adj.set(a, l) }
    l.push(b)
  }
  const chordSegs: [number, number][] = []
  for (const [a, b] of chordList) {
    if (a === b) continue
    addAdj(a, b); addAdj(b, a)
    chordSegs.push([a, b])
  }
  const usedEdge = new Set<string>()
  const ek = (a: number, b: number) => edgeKeyOf(a, b)
  const walkChain = (start: number, next: number): number[] => {
    const out = [start, next]
    usedEdge.add(ek(start, next))
    let cur = next
    let from = start
    for (;;) {
      if (boundary.has(cur)) break
      const ns = (adj.get(cur) ?? []).filter((n) => n !== from && !usedEdge.has(ek(cur, n)))
      if (!ns.length) break
      usedEdge.add(ek(cur, ns[0]))
      out.push(ns[0])
      from = cur
      cur = ns[0]
    }
    return out
  }
  const openChains: number[][] = []
  const closedLoops: number[][] = []
  // chains starting at boundary vertices
  for (const [a, b] of chordSegs) {
    for (const [s, n] of [[a, b], [b, a]] as [number, number][]) {
      if (!boundary.has(s) || usedEdge.has(ek(s, n))) continue
      openChains.push(walkChain(s, n))
    }
  }
  // leftovers: closed loops (or stray interior chains — both ends dangling)
  for (const [a, b] of chordSegs) {
    if (usedEdge.has(ek(a, b))) continue
    const chain = walkChain(a, b)
    if (chain[chain.length - 1] === a) {
      closedLoops.push(chain.slice(0, -1))
      continue
    }
    // both-ends-dangling: extend backward from `a` too
    const backNext = (adj.get(a) ?? []).find((n) => !usedEdge.has(ek(a, n)))
    if (backNext !== undefined) {
      const back = walkChain(a, backNext)
      chain.unshift(...back.slice(1).reverse())
    }
    openChains.push(chain)
  }

  // dangling ends → artificial chord to the nearest boundary vertex whose
  // connector crosses no chord (keeps the region graph planar)
  const crossesAny = (a: Pt2, b: Pt2): boolean =>
    chordSegs.some(([u, v]) => segsCross(a, b, pts[u], pts[v]))
  for (const chain of openChains) {
    for (const end of [0, chain.length - 1]) {
      const id = chain[end]
      if (boundary.has(id)) continue
      const P = pts[id]
      let best = -1, bestD = Infinity
      for (const b of loop) {
        if (chain.includes(b)) continue
        const d = Math.hypot(pts[b][0] - P[0], pts[b][1] - P[1])
        if (d < bestD && !crossesAny(P, pts[b])) { best = b; bestD = d }
      }
      if (best < 0) best = corners[0]
      if (end === 0) chain.unshift(best)
      else chain.push(best)
    }
  }

  // recursive region split
  interface Region { ids: number[]; holes: number[][] }
  const regions: Region[] = [{ ids: loop, holes: [] }]
  const ringOf = (ids: number[]): Pt2[] => ids.map((i) => pts[i])
  for (const chain of openChains) {
    if (chain.length < 2) continue
    const a = chain[0], b = chain[chain.length - 1]
    const mid = midOfChain(chain, pts)
    let target = -1
    for (let ri = 0; ri < regions.length; ri++) {
      const ids = regions[ri].ids
      if (ids.includes(a) && ids.includes(b) && pointInRing(mid, ringOf(ids))) { target = ri; break }
    }
    if (target < 0) {
      // fall back: first region containing both endpoints
      target = regions.findIndex((r) => r.ids.includes(a) && r.ids.includes(b))
      if (target < 0) continue
    }
    const ids = regions[target].ids
    const iA = ids.indexOf(a), iB = ids.indexOf(b)
    const forward: number[] = []
    for (let i = iA; ; i = (i + 1) % ids.length) {
      forward.push(ids[i])
      if (i === iB) break
    }
    const backward: number[] = []
    for (let i = iB; ; i = (i + 1) % ids.length) {
      backward.push(ids[i])
      if (i === iA) break
    }
    const inner = chain.slice(1, -1)
    const r1 = [...forward, ...inner.slice().reverse()]
    const r2 = [...backward, ...inner]
    regions.splice(target, 1, { ids: r1, holes: [] }, { ids: r2, holes: [] })
  }
  for (const hole of closedLoops) {
    if (hole.length < 3) continue
    const p0 = pts[hole[0]]
    const host = regions.find((r) => pointInRing(p0, ringOf(r.ids)))
      ?? regions[0]
    host.holes.push(hole)
    regions.push({ ids: hole, holes: [] })   // the island (the pad interior)
  }

  // earcut each region (holes supported), skip degenerate output
  for (const r of regions) {
    if (r.ids.length < 3) continue
    const idList = [...r.ids]
    const coords: number[] = []
    for (const id of r.ids) { coords.push(pts[id][0], pts[id][1]) }
    const holeIdx: number[] = []
    for (const h of r.holes) {
      holeIdx.push(idList.length)
      for (const id of h) { idList.push(id); coords.push(pts[id][0], pts[id][1]) }
    }
    const tris = earcut(coords, holeIdx.length ? holeIdx : undefined)
    for (let i = 0; i + 2 < tris.length; i += 3) {
      const a = idList[tris[i]], b = idList[tris[i + 1]], c = idList[tris[i + 2]]
      const ar = area2(pts[a], pts[b], pts[c])
      if (Math.abs(ar) < AREA2_EPS) continue
      // delaunator triples are CLOCKWISE in y-up world XY (topoSolid flips
      // them to face up) — emit the same handedness
      if (ar > 0) outTris.push(a, c, b)
      else outTris.push(a, b, c)
    }
  }
}

function midOfChain(chain: number[], pts: Pt2[]): Pt2 {
  // midpoint of the middle chord — strictly inside the region the chain splits
  const i = (chain.length / 2 - 1) | 0
  const a = pts[chain[i]], b = pts[chain[i + 1]]
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]
}

// ---------- seam resolution (the composition layer) ----------

/** rendered-normal winding: topoSolid emits stored (t0,t1,t2) as (t0,t2,t1), so
 *  the RENDERED normal of a stored triple is (P2−P0)×(P1−P0). Order the band
 *  triple so that normal points along `want` (xy). */
function pushBandTri(
  outTris: number[], pts: Pt2[], zs: number[],
  t0: number, t1: number, t2: number, want: Pt2,
): void {
  const P = (i: number): [number, number, number] => [pts[i][0], pts[i][1], zs[i]]
  const A = P(t0), B = P(t2), C = P(t1)
  const ux = B[0] - A[0], uy = B[1] - A[1], uz = B[2] - A[2]
  const vx = C[0] - A[0], vy = C[1] - A[1], vz = C[2] - A[2]
  const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz
  if (nx * want[0] + ny * want[1] >= 0) outTris.push(t0, t1, t2)
  else outTris.push(t0, t2, t1)
}

/** how far into a group the per-group z sample steps off the seam vertex */
const NUDGE = 0.02
/** keep the vertex's exact interpolated z when the group sample agrees within
 *  this — the continuous side stays byte-planar (and byte-identical on the
 *  surgery-only path) */
const KEEP_TOL = 0.05
/** groups whose resolved z agree within this merge back into one vertex */
const MERGE_TOL = 1e-6

/**
 * Resolve every seam at once (see the module header): group each seam vertex's
 * incident facets by around-the-vertex connectivity that never crosses a seam
 * edge, give each group its own z from the graded surface sampled `NUDGE` into
 * the group (keeping the vertex's exact interpolated z when the sample agrees
 * within KEEP_TOL), merge groups whose z agree, give the rest same-xy copies,
 * remap their facets, and stitch one vertical band per seam edge between the
 * two sides — wound toward the LOWER side (the exposed face). Pure — fresh
 * arrays; non-seam facets keep their exact triples and array positions.
 */
export function resolveSeams(
  tin: SurgeryTin,
  seamEdges: [number, number][],
  zAt: (pt: Pt2) => number | null,
): SurgeryTin {
  if (!seamEdges.length) return tin
  const pts: Pt2[] = tin.pts.map((p) => [p[0], p[1]])
  const zs: number[] = tin.zs.slice()
  const tris: number[] = Array.from(tin.tris)
  const nF = (tris.length / 3) | 0

  const ek = (a: number, b: number) => (a < b ? `${a}_${b}` : `${b}_${a}`)

  // a seam pair must be a TRUE mesh edge before it can block groups or carry a
  // band — a chain occasionally skips an intermediate vertex (read-only
  // extraction near coincident features), so expand any non-edge pair through
  // intermediate vertices lying on its segment
  const edgeAll = new Set<string>()
  const neighbors = new Map<number, Set<number>>()
  for (let fi = 0; fi * 3 < tris.length; fi++) {
    const a = tris[fi * 3], b = tris[fi * 3 + 1], c = tris[fi * 3 + 2]
    for (const [u, v] of [[a, b], [b, c], [c, a]]) {
      edgeAll.add(ek(u, v))
      let nu = neighbors.get(u)
      if (!nu) { nu = new Set(); neighbors.set(u, nu) }
      nu.add(v)
      let nv = neighbors.get(v)
      if (!nv) { nv = new Set(); neighbors.set(v, nv) }
      nv.add(u)
    }
  }
  const expanded: [number, number][] = []
  const expandPair = (u: number, v: number, depth: number): void => {
    if (u === v || depth > 64) return
    if (edgeAll.has(ek(u, v))) { expanded.push([u, v]); return }
    // nearest neighbor of u lying ON segment u→v, strictly between
    const U = pts[u], V = pts[v]
    const dx = V[0] - U[0], dy = V[1] - U[1]
    const len2 = dx * dx + dy * dy
    if (len2 < 1e-12) return
    let best = -1, bestT = Infinity
    for (const w of neighbors.get(u) ?? []) {
      if (w === v) continue
      const W = pts[w]
      const t = ((W[0] - U[0]) * dx + (W[1] - U[1]) * dy) / len2
      if (t <= 1e-9 || t >= 1 - 1e-9) continue
      const d = Math.hypot(W[0] - (U[0] + dx * t), W[1] - (U[1] + dy * t))
      if (d > 0.02) continue
      if (t < bestT) { bestT = t; best = w }
    }
    if (best < 0) return   // no path through the mesh — drop (no band possible)
    expanded.push([u, best])
    expandPair(best, v, depth + 1)
  }
  for (const [a, b] of seamEdges) {
    if (a === b) continue
    expandPair(a, b, 0)
  }

  const seamSet = new Set<string>()
  const seamVerts = new Set<number>()
  for (const [a, b] of expanded) {
    if (a === b) continue
    seamSet.add(ek(a, b))
    seamVerts.add(a)
    seamVerts.add(b)
  }

  // incident facets per seam vertex + the two facets per seam edge (pre-remap)
  const incident = new Map<number, number[]>()
  const edgeFacets = new Map<string, number[]>()
  for (let fi = 0; fi < nF; fi++) {
    const a = tris[fi * 3], b = tris[fi * 3 + 1], c = tris[fi * 3 + 2]
    for (const v of [a, b, c]) {
      if (!seamVerts.has(v)) continue
      const l = incident.get(v)
      if (l) l.push(fi)
      else incident.set(v, [fi])
    }
    for (const [u, v] of [[a, b], [b, c], [c, a]]) {
      const k = ek(u, v)
      if (!seamSet.has(k)) continue
      const l = edgeFacets.get(k)
      if (l) l.push(fi)
      else edgeFacets.set(k, [fi])
    }
  }
  // remember where u and v sit in each seam edge's facets BEFORE any remap, so
  // the band can read the remapped ids back by position
  const edgeSlots = new Map<string, { fi: number; su: number; sv: number }[]>()
  for (const [k, fis] of edgeFacets) {
    const [uS, vS] = k.split('_').map(Number)
    edgeSlots.set(k, fis.map((fi) => {
      let su = -1, sv = -1
      for (let s = 0; s < 3; s++) {
        if (tris[fi * 3 + s] === uS) su = s
        if (tris[fi * 3 + s] === vS) sv = s
      }
      return { fi, su, sv }
    }))
  }

  const centroid = (fi: number): Pt2 => {
    const a = tris[fi * 3], b = tris[fi * 3 + 1], c = tris[fi * 3 + 2]
    return [
      (pts[a][0] + pts[b][0] + pts[c][0]) / 3,
      (pts[a][1] + pts[b][1] + pts[c][1]) / 3,
    ]
  }

  // PHASE 1 — per seam vertex: group incident facets and resolve per-group z,
  // all against the PRISTINE mesh. Nothing is written yet: remaps and z writes
  // are collected and applied together in phase 2, so the grouping of one
  // vertex can never see another vertex's remapped corners (interleaving them
  // made the seam test `ek(v, other)` miss already-remapped neighbors and the
  // flood crossed the seam — the residual Forsyth tears along shared
  // pad/pad/wall lines).
  const remaps: { fi: number; from: number; to: number }[] = []
  const zWrites: { v: number; z: number }[] = []
  const newVerts: { xy: Pt2; z: number }[] = []
  const nOriginal = pts.length
  for (const v of seamVerts) {
    const fs = incident.get(v)
    if (!fs || fs.length < 2) continue
    const P = pts[v]
    // adjacency around v: facets sharing a NON-SEAM edge (v, x) connect
    const byOther = new Map<number, number[]>()
    const othersOf = (fi: number): number[] => {
      const a = tris[fi * 3], b = tris[fi * 3 + 1], c = tris[fi * 3 + 2]
      return a === v ? [b, c] : b === v ? [a, c] : [a, b]
    }
    for (const fi of fs) {
      for (const other of othersOf(fi)) {
        const l = byOther.get(other)
        if (l) l.push(fi)
        else byOther.set(other, [fi])
      }
    }
    const groupOf = new Map<number, number>()
    let nGroups = 0
    for (const seed of fs) {
      if (groupOf.has(seed)) continue
      const g = nGroups++
      const stack = [seed]
      groupOf.set(seed, g)
      while (stack.length) {
        const fi = stack.pop()!
        for (const other of othersOf(fi)) {
          if (seamSet.has(ek(v, other))) continue   // never cross a seam
          for (const nb of byOther.get(other) ?? []) {
            if (!groupOf.has(nb)) { groupOf.set(nb, g); stack.push(nb) }
          }
        }
      }
    }
    if (nGroups < 2) continue
    // per-group z: the graded surface sampled a hair into the group. The sample
    // steps from the vertex toward a MEMBER FACET'S OWN CENTROID — a point on
    // the vertex→centroid segment is strictly inside that triangle, so it is on
    // the group's side of every seam BY CONSTRUCTION. (A first cut averaged the
    // group's centroid directions instead; around a narrow pad-corner wedge the
    // wide outside fan's average pointed INTO the wedge, both sides sampled the
    // pad elevation, and the sides merged — the outside surface sagged to the
    // pad.) The largest member facet gives the most stable interior.
    const groupZ: number[] = new Array(nGroups)
    const groupBest: number[] = new Array(nGroups).fill(-1)
    const groupBestArea: number[] = new Array(nGroups).fill(-1)
    for (const fi of fs) {
      const g = groupOf.get(fi)!
      const a = tris[fi * 3], b = tris[fi * 3 + 1], c = tris[fi * 3 + 2]
      const ar = Math.abs(area2(pts[a], pts[b], pts[c]))
      if (ar > groupBestArea[g]) { groupBestArea[g] = ar; groupBest[g] = fi }
    }
    for (let g = 0; g < nGroups; g++) {
      const cen = centroid(groupBest[g])
      const d = Math.hypot(cen[0] - P[0], cen[1] - P[1])
      if (d < 1e-9) { groupZ[g] = zs[v]; continue }
      const step = Math.min(NUDGE, d * 0.5) / d
      const sampled = zAt([P[0] + (cen[0] - P[0]) * step, P[1] + (cen[1] - P[1]) * step])
      groupZ[g] = sampled === null || Math.abs(sampled - zs[v]) < KEEP_TOL ? zs[v] : sampled
    }
    // merge groups with agreeing z; the first surviving value keeps the
    // original id (hull entries referencing v stay valid)
    const vertexFor = new Map<number, number>()
    const values: { z: number; id: number }[] = []
    for (let g = 0; g < nGroups; g++) {
      const got = values.find((x) => Math.abs(x.z - groupZ[g]) < MERGE_TOL)
      if (got) { vertexFor.set(g, got.id); continue }
      let id: number
      if (!values.length) {
        id = v
        zWrites.push({ v, z: groupZ[g] })
      } else {
        id = nOriginal + newVerts.length
        newVerts.push({ xy: [P[0], P[1]], z: groupZ[g] })
      }
      values.push({ z: groupZ[g], id })
      vertexFor.set(g, id)
    }
    if (values.length < 2) continue
    for (const fi of fs) {
      const id = vertexFor.get(groupOf.get(fi)!)!
      if (id === v) continue
      remaps.push({ fi, from: v, to: id })
    }
  }
  // PHASE 2 — apply everything at once
  for (const nv of newVerts) {
    pts.push(nv.xy)
    zs.push(nv.z)
  }
  for (const w of zWrites) zs[w.v] = w.z
  for (const r of remaps) {
    for (let k = 0; k < 3; k++) if (tris[r.fi * 3 + k] === r.from) tris[r.fi * 3 + k] = r.to
  }

  // one vertical band per seam edge, between the two sides' (possibly remapped)
  // corner ids — wound toward the LOWER side (the exposed face)
  const banded = new Set<string>()
  for (const [u, v] of expanded) {
    if (u === v) continue
    const k = ek(u, v)
    if (banded.has(k)) continue
    banded.add(k)
    const slots = edgeSlots.get(k)
    if (!slots || slots.length !== 2) continue   // hull seams have no far side
    const [uA, vA] = [tris[slots[0].fi * 3 + slots[0].su], tris[slots[0].fi * 3 + slots[0].sv]]
    const [uB, vB] = [tris[slots[1].fi * 3 + slots[1].su], tris[slots[1].fi * 3 + slots[1].sv]]
    if (uA === uB && vA === vB) continue
    if (Math.abs(zs[uA] - zs[uB]) < MERGE_TOL && Math.abs(zs[vA] - zs[vB]) < MERGE_TOL) continue
    const zSideA = (zs[uA] + zs[vA]) / 2
    const zSideB = (zs[uB] + zs[vB]) / 2
    const lower = zSideA <= zSideB ? slots[0].fi : slots[1].fi
    const cen = centroid(lower)
    const mid: Pt2 = [(pts[u][0] + pts[v][0]) / 2, (pts[u][1] + pts[v][1]) / 2]
    const wl = Math.hypot(cen[0] - mid[0], cen[1] - mid[1]) || 1
    const want: Pt2 = [(cen[0] - mid[0]) / wl, (cen[1] - mid[1]) / wl]
    if (vA !== vB) pushBandTri(tris, pts, zs, uA, vA, vB, want)
    if (uA !== uB) pushBandTri(tris, pts, zs, uA, vB, uB, want)
  }

  return { pts, zs, tris, hull: tin.hull.slice() }
}


